import React from 'react';

function LineChart({ data, metric, height = 250 }) {
  if (!data || data.length < 2) {
    return (
      <div className="chart-placeholder" style={{ height: `${height}px` }}>
        <div className="no-data">Need at least 2 data points</div>
      </div>
    );
  }

  // Sort data by date (newest first, then reverse for chart)
  const sortedData = [...data].sort((a, b) => new Date(b.snapshot_date) - new Date(a.snapshot_date)).reverse();
  
  // Get metric values
  const values = sortedData.map(item => {
    switch (metric) {
      case 'infinity': return item.infinity_total;
      case 'monthly': return item.monthly_total;
      case 'community': return item.community_size;
      default: return item.infinity_total;
    }
  });

  // Calculate chart dimensions
  const width = 800;
  const padding = { top: 20, right: 40, bottom: 40, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Find min/max values
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const valueRange = maxValue - minValue || 1;

  // Generate path points
  const points = values.map((value, index) => {
    const x = padding.left + (index / (values.length - 1)) * chartWidth;
    const y = padding.top + chartHeight - ((value - minValue) / valueRange) * chartHeight;
    return { x, y, value };
  });

  // Create SVG path
  const pathData = points.reduce((path, point, index) => {
    return path + (index === 0 ? `M ${point.x} ${point.y}` : ` L ${point.x} ${point.y}`);
  }, '');

  // Create area path (for fill)
  const areaPath = pathData + 
    ` L ${points[points.length - 1].x} ${padding.top + chartHeight}` +
    ` L ${points[0].x} ${padding.top + chartHeight}` +
    ' Z';

  // Format metric labels
  const formatValue = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  // Format dates
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="line-chart">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Grid lines */}
        <defs>
          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
          </pattern>
        </defs>
        <rect width={width} height={height} fill="url(#grid)" />
        
        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
          const value = minValue + (valueRange * ratio);
          const y = padding.top + chartHeight - (ratio * chartHeight);
          return (
            <g key={index}>
              <text 
                x={padding.left - 10} 
                y={y + 4} 
                textAnchor="end" 
                fontSize="11" 
                fill="#666"
              >
                {formatValue(value)}
              </text>
              <line 
                x1={padding.left} 
                y1={y} 
                x2={width - padding.right} 
                y2={y} 
                stroke="rgba(255,255,255,0.1)" 
                strokeWidth="1"
              />
            </g>
          );
        })}
        
        {/* X-axis labels */}
        {points.map((point, index) => {
          if (index % Math.ceil(points.length / 6) === 0) {
            return (
              <text 
                key={index}
                x={point.x} 
                y={height - padding.bottom + 20} 
                textAnchor="middle" 
                fontSize="10" 
                fill="#666"
              >
                {formatDate(sortedData[index].snapshot_date)}
              </text>
            );
          }
          return null;
        })}
        
        {/* Area fill */}
        <path 
          d={areaPath} 
          fill="url(#gradient)" 
          opacity="0.2"
        />
        
        {/* Line */}
        <path 
          d={pathData} 
          fill="none" 
          stroke="#F59E0B" 
          strokeWidth="2" 
          strokeLinecap="round"
        />
        
        {/* Data points */}
        {points.map((point, index) => (
          <circle 
            key={index}
            cx={point.x} 
            cy={point.y} 
            r="4" 
            fill="#F59E0B"
            className="chart-point"
          />
        ))}
        
        {/* Gradient definition */}
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.3"/>
            <stop offset="100%" stopColor="#F59E0B" stopOpacity="0"/>
          </linearGradient>
        </defs>
      </svg>
      
      <style jsx>{`
        .chart-point {
          transition: r 0.2s;
        }
        .chart-point:hover {
          r: 6;
        }
      `}</style>
    </div>
  );
}

export default LineChart;
